import { SelectChangeEvent } from "@mui/material";
import { createContext, FC, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  deleteUserById,
  getAllOrganizations,
  getAllPaises,
  getRolesByOrgId,
  getUsersByOrgId,
  postUser,
  postUserInOrganization,
  postUsersInRole,
  putUserById,
} from "../../api/broker";
import { useAuthContext } from "../../hooks/Auth";
import { UsersContextProps } from "../../interfaces/SuperAdmin/ContextProps/iUser";
import { iDataOrganizacion } from "../../interfaces/SuperAdmin/Pages/iDataOrganizacion";
import { iDataRoles } from "../../interfaces/SuperAdmin/Pages/iDataRoles";
import {
  iDataUser,
  PostUserFields,
  PutUserFields,
} from "../../interfaces/SuperAdmin/Pages/iDataUser";
import { iDataPaises } from "../../interfaces/SuperAdmin/Others/iDataPaises";

const UsersContext = createContext<UsersContextProps>({} as UsersContextProps);

interface Props {
  children: React.ReactNode;
}

const UsersProvider: FC<Props> = ({ children }) => {
  const { idOrganization } = useParams();
  const { canAccess } = useAuthContext();

  const [organizaciones, setOrganizaciones] = useState<iDataOrganizacion[]>([]);
  const [roles, setRoles] = useState<iDataRoles[]>([]);
  const [paises, setPaises] = useState<iDataPaises[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(
    idOrganization
  );

  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);

  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [toDeleteUser, setToDeleteUser] = useState("");

  const [usuarios, setUsuarios] = useState<iDataUser[]>([]);
  const [usuarioSeleccionado, setUsuarioSeleccionado] =
    useState<iDataUser | null>(null);
  const [filteredUsers, setFilteredUsers] = useState<iDataUser[]>([]);

  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();

  // Fetch de organizaciones para seleccionar
  const fetchOrgs = async () => {
    setIsLoadingOrgs(true);
    try {
      const orgsRes = await getAllOrganizations();
      if (orgsRes.data.statusCode === 200) {
        setOrganizaciones(orgsRes.data.body);
      } else {
        toast.error("Error al obtener las organizaciones");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener las organizaciones");
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  useEffect(() => {
    const isSuperAdmin = canAccess("view:users");
    if (isSuperAdmin) fetchOrgs();
  }, []);

  useEffect(() => {
    const fetchUsers = async (id: string) => {
      setIsLoadingUsers(true);
      try {
        const [userRes, rolesRes, paisRes] = await Promise.all([
          getUsersByOrgId(id),
          getRolesByOrgId(id),
          getAllPaises(),
        ]);
        if (userRes.data.statusCode == 200) {
          setUsuarios(userRes.data.body);
        } else {
          toast.error("Error al obtener los usuarios");
        }

        if (rolesRes.data.statusCode === 200) {
          setRoles(rolesRes.data.body);
        } else {
          toast.error("Error al obtener los roles");
        }

        if (paisRes.data.statusCode === 200) {
          setPaises(paisRes.data.body);
        } else {
          toast.error("Error al obtener paises");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener los usuarios, paises y/o roles");
      } finally {
        setIsLoadingUsers(false);
      }
    };

    if (selectedOrg) {
      fetchUsers(selectedOrg);
    }
  }, [selectedOrg]);

  const handleSelectChange = async (event: SelectChangeEvent) => {
    setSelectedOrg(event.target.value);
    navigate(`/asistente/users/${event.target.value}`);
  };

  const handleSearchQuery = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchQuery(e.target.value);
  };

  useEffect(() => {
    const normalizedSearch = searchQuery.toLowerCase().trim();
    setFilteredUsers(() => {
      return usuarios.filter((user) => {
        const fullName = `${user.nombres} ${user.apellidos}`.toLowerCase();
        const email = user.email.toLowerCase();
        return (
          fullName.includes(normalizedSearch) ||
          email.includes(normalizedSearch)
        );
      });
    });
  }, [searchQuery, usuarios]);

  const assignToDeleteUser = (userId: string) => {
    setToDeleteUser(userId);
  };

  const handleDelete = async () => {
    if (toDeleteUser) {
      setIsDeleting(true);
      try {
        const res = await deleteUserById(toDeleteUser);
        console.log(res);
        if (res.data.statusCode === 200) {
          const newArr = usuarios.filter(
            (user) => user.id_usuario !== toDeleteUser
          );
          setUsuarios(newArr);
          setToDeleteUser("");
          toast.success("Usuario borrado exitosamente");
        } else {
          toast.error("Error al borrar usuario");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al borrar usuario");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const toggleCreateModal = (isOpen: boolean) => {
    setIsCreateModalOpen(isOpen);
  };

  const userEdit = async (id_usuario: string, data: PutUserFields) => {
    setIsEditing(true);
    try {
      const res = await putUserById(id_usuario, data);
      console.log(res);
      if (res.data.statusCode === 200) {
        setUsuarios((prev) =>
          prev.map((user) => {
            if (user.id_usuario === id_usuario) {
              return res.data.body;
            }
            return user;
          })
        );
        setIsEditModalOpen(false);
        toast.success("Usuario editado exitosamente");
      } else {
        toast.error("Error al editar usuario");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al editar usuario");
    } finally {
      setIsEditing(false);
    }
  };

  const userCreate = async (user: PostUserFields, roles: string[]) => {
    if (!selectedOrg) return;
    setIsCreating(true);
    try {
      //Creamos el usuario
      const userRes = await postUser(user);
      const { statusCode, body } = userRes.data;
      console.log(userRes);

      if (statusCode === 200) {
        //Agregamos el usuario a la lista
        toast.success("Usuario registrado exitosamente en las DB");
        const rdsUser = body.rds;

        //Creamos la relación usuario-organizacion
        const orgRes = await postUserInOrganization(
          rdsUser.id_usuario,
          selectedOrg
        );

        if (orgRes.data.statusCode === 201) {
          toast.success("Usuario agregado exitosamente a la organización");
        } else {
          toast.error("Error al agregar usuario a la organización");
        }

        // Creamos los roles
        const rolesRes = await Promise.all(
          roles.map((roleId) =>
            postUsersInRole({
              id_rol: roleId,
              id_usuarios: [rdsUser.id_usuario],
            })
          )
        );
        setUsuarios((prev) => [...prev, rdsUser]);
        console.log(rolesRes);

        const someResFailed = rolesRes.some(
          (role) => role.data.statusCode !== 201
        );

        if (someResFailed) {
          toast.error("Error al agregar el usuario a los roles");
        } else {
          toast.success("Usuario anexado exitosamente a los roles");
        }

        toggleCreateModal(false);
      } else {
        toast.error("Error al registrar el usuario en las DB");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al registrar el usuario en las DB");
    } finally {
      setIsCreating(false);
    }
  };

  const toggleEditModal = (isOpen: boolean) => {
    if (!isOpen) {
      setUsuarioSeleccionado(null);
    }
    setIsEditModalOpen(isOpen);
  };

  const handleEdit = (user: iDataUser) => {
    setUsuarioSeleccionado(user);
    toggleEditModal(true);
  };

  return (
    <UsersContext.Provider
      value={{
        organizaciones,
        selectedOrg,
        isLoadingOrgs,
        searchQuery,
        isLoadingUsers,
        filteredUsers,
        usuarioSeleccionado,
        isCreateModalOpen,
        isEditModalOpen,
        toDeleteUser,
        isCreating,
        isEditing,
        isDeleting,
        roles,
        paises,
        handleSelectChange,
        handleSearchQuery,
        handleDelete,
        toggleCreateModal,
        toggleEditModal,
        handleEdit,
        userCreate,
        userEdit,
        assignToDeleteUser,
      }}
    >
      {children}
    </UsersContext.Provider>
  );
};

const useSuperAdminUsers = () => useContext(UsersContext);

export { UsersProvider, useSuperAdminUsers };
